import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { useCustomer, useCustomerSearch } from '@/routes/customers/useCustomers';

const SEARCH_SETTLE_MS = 250;

/**
 * The invoice list's customer filter (M2-12): type to search, pick one, and
 * the list narrows to them. The search is the customers endpoint's own —
 * server-side, the same one New estimate uses — because an account's
 * customers are too many for a select.
 *
 * Nothing is filtered while the box holds text nobody has picked: a filter by
 * whatever is half-typed would be a filter by a customer nobody chose.
 *
 * The pick lives in the URL (the list's filters do), so the box can be handed
 * a customer it never searched for — Back, or the invoice page's link. While
 * one is picked the box shows THEIR name: remembered from the pick, or read
 * from the customer when it came from the URL. `term` is only ever what was
 * typed.
 */
export function CustomerFilter({
  customerId,
  onChange,
}: {
  customerId: string;
  onChange: (customerId: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [term, setTerm] = useState('');
  // Asked once the typing pauses, not per keystroke — the Customers screen's
  // debounce (review of pass B).
  const [settled, setSettled] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSettled(term), SEARCH_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [term]);
  const customers = useCustomerSearch(settled, settled.trim().length > 0 && customerId === '');
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  const fromUrl = useCustomer(
    customerId !== '' && picked?.id !== customerId ? customerId : undefined,
  );
  const pickedName = picked?.id === customerId ? picked.name : (fromUrl.data?.name ?? '');

  return (
    <div className="relative text-sm">
      <label htmlFor={`${id}-customer`} className="mb-1 block font-medium">
        {t('invoices.filters.customer')}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={`${id}-customer`}
          className={FIELD_CLASS}
          value={customerId !== '' ? pickedName : term}
          placeholder={t('invoices.filters.customerSearch')}
          autoComplete="off"
          onChange={(event) => {
            setTerm(event.target.value);
            if (customerId !== '') onChange('');
          }}
        />
        {customerId !== '' ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setTerm('');
              onChange('');
            }}
          >
            {t('invoices.filters.clearCustomer')}
          </Button>
        ) : null}
      </div>
      {term.trim() && customerId === '' && customers.data ? (
        customers.data.length === 0 ? (
          <p className="mt-1 text-[var(--color-muted-foreground)]">
            {t('invoices.filters.noCustomers')}
          </p>
        ) : (
          <ul
            aria-label={t('invoices.filters.customerMatches')}
            className="absolute z-10 mt-1 max-h-48 w-full overflow-auto rounded-md border border-[var(--color-border)] bg-[var(--color-background)]"
          >
            {customers.data.slice(0, 8).map((customer) => (
              <li key={customer.id}>
                <button
                  type="button"
                  className="block w-full px-3 py-2 text-left hover:bg-[var(--color-muted)]"
                  onClick={() => {
                    setPicked({ id: customer.id, name: customer.name });
                    setTerm('');
                    onChange(customer.id);
                  }}
                >
                  {customer.name}
                </button>
              </li>
            ))}
          </ul>
        )
      ) : null}
    </div>
  );
}
